import JWTManager from '../constant/JWTManager';

// Action Types
export const LOGIN_STATUS = 'LOGIN_STATUS'
export const LOGIN_RESET = 'LOGIN_RESET'
export const LOGOUT = 'LOGOUT'
export const SAVE_MEMBER = 'SAVE_MEMBER'
export const SAVE_UPLOADED_FILE = 'SAVE_UPLOADED_FILE'
export const SAVE_FILE_INFO = 'SAVE_FILE_INFO'
export const SAVE_CLUB_INFO = 'SAVE_CLUB_INFO'
export const SAVE_UNION_INFO = 'SAVE_UNION_INFO'
export const SAVE_REVENUE = 'SAVE_REVENUE'
export const SAVE_UNIONS_FEE = 'SAVE_UNIONS_FEE'
export const SAVE_GAMES_FEE = 'SAVE_GAMES_FEE'
export const SAVE_GENERAL_REPORT_STATUS = 'SAVE_GENERAL_REPORT_STATUS'
export const SAVE_SUPERAGENTS_FEE = 'SAVE_SUPERAGENTS_FEE'
export const SAVE_JSON_WARNING = "SAVE_JSON_WARNING"
export const SAVE_MEMBERSHIP_SUM = "SAVE_MEMBERSHIP_SUM"
export const SAVE_UNION_MEMBERSHIP = "SAVE_UNION_MEMBERSHIP"
export const SAVE_USER = "SAVE_USER"
export const SAVE_WEEKLY_TOTAL_PAY = 'SAVE_WEEKLY_TOTAL_PAY'
export const WEEKLY_TOTAL_PAY_FAILED = 'WEEKLY_TOTAL_PAY_FAILED'
export const SAVE_R2H_REVENUE = "SAVE_R2H_REVENUE"
export const SAVE_WEEKLY_TOTAL_WIN = "SAVE_WEEKLY_TOTAL_WIN"
export const WEEKLY_TOTAL_WIN_FAILED = "WEEKLY_TOTAL_WIN_FAILED"
export const RESET_WEEKLY_TOTAL_WIN_DATA = "RESET_WEEKLY_TOTAL_WIN_DATA"

// Add fetch status actions
export const FETCH_START = 'FETCH_START'
export const FETCH_SUCCESS = 'FETCH_SUCCESS'
export const FETCH_FAILURE = 'FETCH_FAILURE'

export const SAVE_SERVER_CONFIG = "SAVE_SERVER_CONFIG"
export const CLEAR_MULTI_PERIOD_DATA = "CLEAR_MULTI_PERIOD_DATA"

export const SAVE_FLASH_LEVEL_DATA = "SAVE_FLASH_LEVEL_DATA"
export const FLASH_LEVEL_DATA_FAILED = "FLASH_LEVEL_DATA_FAILED"

export const SAVE_AGENT_TAX_EVENT_LIST = "SAVE_AGENT_TAX_EVENT_LIST"
export const AGENT_TAX_EVENT_LIST_FAILED = "AGENT_TAX_EVENT_LIST_FAILED"

// Token handling utility functions - 使用 JWTManager
const getAccessToken = () => JWTManager.getAccessToken();
const getRefreshToken = () => JWTManager.getRefreshToken();
const setTokens = (accessToken, refreshToken, expiresIn) => {
  JWTManager.setToken({ access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn });
};
const clearTokens = () => {
  JWTManager.clearToken();
};
const isTokenExpired = () => {
  return JWTManager.isTokenExpired();
};

// Enhanced fetchAction with token refresh capability
const fetchWithAuth = async (url, options = {}) => {
  // Add auth header if not already present
  if (!options.headers) {
    options.headers = {};
  }
  
  // Check if token is expired and try to refresh if needed
  if (isTokenExpired() && getRefreshToken()) {
    try {
      const refreshed = await refreshTokenRequest();
      if (!refreshed) {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      clearTokens();
      throw new Error('Authentication failed');
    }
  }
  
  // Add current access token to request
  options.headers['Authorization'] = `Bearer ${getAccessToken()}`;
  
  // Make the request
  const response = await fetch(url, options);
  
  // Check if we should parse as JSON or return raw response
  if (options.raw) {
    return response;
  }
  
  // Handle authentication errors
  if (response.status === 401) {
    // Try refresh token if we have one
    if (getRefreshToken()) {
      try {
        const refreshed = await refreshTokenRequest();
        if (refreshed) {
          // Retry the original request with new token
          options.headers['Authorization'] = `Bearer ${getAccessToken()}`;
          const retryResponse = await fetch(url, options);
          return options.raw ? retryResponse : retryResponse.json();
        }
      } catch (error) {
        clearTokens();
        throw new Error('Authentication failed');
      }
    }
    
    clearTokens();
    throw new Error('Authentication failed');
  }
  
  return response.json();
};

// Helper function to handle token refresh
const refreshTokenRequest = async () => {
  try {
    const response = await fetch('/api/refresh', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getRefreshToken()}`
      }
    });
    
    const data = await response.json();
    
    if (data.code === 1 && data.access_token) {
      // 使用 JWTManager 更新 token（包含自動刷新排程）
      JWTManager.updateAccessToken(data.access_token, data.expires_in || 7200);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

// Updated fetchAction using the new fetchWithAuth and fetch status tracking
const fetchAction = (api, body, successAction, successFunction, failAction) => async (dispatch, getState) => {
  // Dispatch fetch start action
  dispatch({ 
    type: FETCH_START, 
    payload: { action: api } 
  });
  
  try {
    const options = {
      method: body === null ? 'GET' : 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    };
    
    if (body !== null) {
      options.body = body;
    }
    
    const data = await fetchWithAuth(`/api/${api}`, options);
    
    // Action Succeed
    if (data.code === 1) {
      // Dispatch fetch success action
      dispatch({ 
        type: FETCH_SUCCESS, 
        payload: { action: api } 
      });
      
      if (successAction !== null) {
        dispatch({ type: successAction, payload: data });
      } else if (successFunction) {
        successFunction(data);
      }
    } 
    // Action Failed with specific error
    else if (data.code === -1) {
      console.error('API Error:', data.msg);
      
      // Dispatch fetch failure action
      dispatch({ 
        type: FETCH_FAILURE, 
        payload: { action: api, error: data.msg } 
      });
      
      if (failAction) {
        dispatch({ type: failAction, payload: data });
      }
    }
    // Other failures
    else if (failAction) {
      // Dispatch fetch failure action
      dispatch({ 
        type: FETCH_FAILURE, 
        payload: { action: api, error: 'Unknown error' } 
      });
      
      dispatch({ type: failAction, payload: data });
    }
  } catch (error) {
    console.error('Fetch error:', error.message);
    
    // Dispatch fetch failure action
    dispatch({ 
      type: FETCH_FAILURE, 
      payload: { action: api, error: error.message } 
    });
    
    // If authentication error, clear session and redirect
    if (error.message === 'Authentication failed') {
      clearTokens();
      dispatch({ type: 'LOGIN_FAILED', payload: { errormsg: 'Authentication failed' } });
    } else if (failAction) {
      dispatch({ type: failAction, payload: { error: error.message } });
    }
  }
};

// JSON-specific fetchAction with fetch status tracking
const fetchJSONAction = (api, body, successAction, successFunction, failAction) => async (dispatch, getState) => {
  // Dispatch fetch start action
  dispatch({ 
    type: FETCH_START, 
    payload: { action: api } 
  });
  
  try {
    const options = {
      method: 'POST',
      mode: 'cors',
      headers: { 
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    };
    
    const data = await fetchWithAuth(`/api/${api}`, options);
    
    // Action Succeed
    if (data.code === 1) {
      // Dispatch fetch success action
      dispatch({ 
        type: FETCH_SUCCESS, 
        payload: { action: api } 
      });
      
      if (successAction !== null) {
        dispatch({ type: successAction, payload: data });
      } else if (successFunction) {
        successFunction(data);
      }
    } 
    // Action Failed with specific error
    else if (data.code === -1) {
      console.error('API Error:', data.msg);
      
      // Dispatch fetch failure action
      dispatch({ 
        type: FETCH_FAILURE, 
        payload: { action: api, error: data.msg } 
      });
      
      if (failAction) {
        dispatch({ type: failAction, payload: data });
      }
    }
    // Other failures
    else if (failAction) {
      // Dispatch fetch failure action
      dispatch({ 
        type: FETCH_FAILURE, 
        payload: { action: api, error: 'Unknown error' } 
      });
      
      dispatch({ type: failAction, payload: data });
    }
  } catch (error) {
    console.error('Fetch error:', error.message);
    
    // Dispatch fetch failure action
    dispatch({ 
      type: FETCH_FAILURE, 
      payload: { action: api, error: error.message } 
    });
    
    // If authentication error, clear session and redirect
    if (error.message === 'Authentication failed') {
      clearTokens();
      dispatch({ type: 'LOGIN_FAILED', payload: { errormsg: 'Authentication failed' } });
    } else if (failAction) {
      dispatch({ type: failAction, payload: { error: error.message } });
    }
  }
};

export const login = (username, password) => async (dispatch) => {
  // Dispatch fetch start action
  dispatch({ 
    type: FETCH_START, 
    payload: { action: 'login' } 
  });
  
  try {
    let body = new FormData();
    body.append('username', username);
    body.append('password', password);
    
    const response = await fetch('/api/login', { 
      method: 'POST', 
      mode: 'cors', 
      body: body 
    });
    
    const data = await response.json();
    
    if (data.code === 1 && data.access_token && data.refresh_token) {
      // Dispatch fetch success action
      dispatch({ 
        type: FETCH_SUCCESS, 
        payload: { action: 'login' } 
      });
      
      // Store tokens and expiry time
      setTokens(data.access_token, data.refresh_token, data.expires_in);
      
      dispatch({ type: 'LOGIN_SUCCESS', payload: data });
      return true;
    } else {
      // Dispatch fetch failure action
      dispatch({ 
        type: FETCH_FAILURE, 
        payload: { action: 'login', error: data.msg || 'Login failed' } 
      });
      
      dispatch({ 
        type: 'LOGIN_FAILED', 
        payload: { errormsg: data.msg || 'Login failed' } 
      });
      return false;
    }
  } catch (error) {
    console.error('Login error:', error);
    
    // Dispatch fetch failure action
    dispatch({ 
      type: FETCH_FAILURE, 
      payload: { action: 'login', error: 'Connection failed' } 
    });
    
    clearTokens();
    dispatch({ 
      type: 'LOGIN_FAILED', 
      payload: { errormsg: 'Connection failed' } 
    });
    return false;
  }
};

// Updated logout to match the new API
export const logout = () => async (dispatch) => {
  // Dispatch fetch start action
  dispatch({ 
    type: FETCH_START, 
    payload: { action: 'logout' } 
  });
  
  try {
    const body = new FormData();
    body.append('access_token', getAccessToken());
    
    const options = {
      method: 'POST',
      mode: 'cors',
      headers: { 'Authorization': `Bearer ${getRefreshToken()}` },
      body: body
    };
    
    await fetch('/api/logout', options);
    
    // Dispatch fetch success action
    dispatch({ 
      type: FETCH_SUCCESS, 
      payload: { action: 'logout' } 
    });
    
    // Clear tokens from storage regardless of response
    clearTokens();
    dispatch({ type: 'LOGIN_RESET' });
    
    return true;
  } catch (error) {
    console.error('Logout error:', error);
    
    // Dispatch fetch failure action
    dispatch({ 
      type: FETCH_FAILURE, 
      payload: { action: 'logout', error: 'Logout failed' } 
    });
    
    // Still clear tokens even if request fails
    clearTokens();
    dispatch({ type: 'LOGIN_RESET' });
    
    return false;
  }
};

// File upload action
export const uploadFile = (file) => async (dispatch) => {
  // Dispatch fetch start action
  dispatch({ 
    type: FETCH_START, 
    payload: { action: 'uploadFile' } 
  });
  
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const options = {
      method: 'POST',
      body: formData,
    };
    
    const data = await fetchWithAuth('/api/uploadFile', options);
    
    if (data.code === 1) {
      // Dispatch fetch success action
      dispatch({ 
        type: FETCH_SUCCESS, 
        payload: { action: 'uploadFile' } 
      });
      
      // Refresh file lists
      dispatch(listUploadedFile());
      dispatch(listDBFileInfo());
      return true;
    }
    
    // Dispatch fetch failure action
    dispatch({ 
      type: FETCH_FAILURE, 
      payload: { action: 'uploadFile', error: data.msg || 'Upload failed' } 
    });
    
    return false;
  } catch (error) {
    console.error('Upload error:', error);
    
    // Dispatch fetch failure action
    dispatch({ 
      type: FETCH_FAILURE, 
      payload: { action: 'uploadFile', error: error.message } 
    });
    
    return false;
  }
};

// // Helper function for parsing form data
// function parsePostBody(details) {
//   let body = new FormData();
//   Object.keys(details).forEach((item) => body.append(item, details[item]));
//   return body;
// }

// Other API actions kept similar but using the enhanced fetchAction
export const getUser = () => (dispatch) => {
  dispatch(silentFetchAction('getUser', null, SAVE_USER));
};

export const listData = () => (dispatch) => {
  dispatch(fetchAction('listMember', null, SAVE_MEMBER));
};

export const getMemberById = (id) => (dispatch) => {
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('searchMember', { 'id': id }, SAVE_MEMBER));
};

export const listRevenue = () => (dispatch) => {
  dispatch(fetchAction('listRevenue', null, SAVE_REVENUE));
};

export const listR2HRevenue = () => (dispatch) => {
  dispatch(fetchAction('listR2HRevenue', null, SAVE_R2H_REVENUE));
};

export const generateReport = () => (dispatch) => {
  dispatch(fetchAction('generateReport', null, null));
};

export const getReportStatus = () => (dispatch) => {
  dispatch(fetchAction('getReportStatus', null, SAVE_GENERAL_REPORT_STATUS));
};

export const getJsonWarning = () => (dispatch) => {
  dispatch(fetchAction('getJsonWarning', null, SAVE_JSON_WARNING));
};

export const listUploadedFile = () => (dispatch) => {
  dispatch(fetchAction('listFile', null, SAVE_UPLOADED_FILE));
};

export const listDBFileInfo = (page = 1, pageSize = 100) => (dispatch) => {
  dispatch(fetchAction(`listDBFileInfo?page=${page}&page_size=${pageSize}`, null, SAVE_FILE_INFO));
};

export const listUnionInfo = () => (dispatch) => {
  dispatch(fetchAction('listUnionInfo', null, SAVE_UNION_INFO));
};

export const addUnionInfo = (union) => (dispatch) => {
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('addUnionInfo', union, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const updateUnionInfo = (union) => (dispatch) => {
  delete union['clubs'];
  delete union['APlist'];
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('updateUnionInfo', union, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const deleteUnionInfo = (id) => (dispatch) => {
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('deleteUnionInfo', { 'id': id }, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const addClubInfo = (unionID, club) => (dispatch) => {
  club["unionID"] = unionID;
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('addClubInfo', club, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const updateClubInfo = (unionID, club) => (dispatch) => {
  club["unionID"] = unionID;
  delete club['APlist'];
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('updateClubInfo', club, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const deleteClubInfo = (unionID, id) => (dispatch) => {
  const body = {
    'id': id,
    'unionID': unionID
  };
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('deleteClubInfo', body, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const addAgentInfo = (unionID, clubID, agent) => (dispatch) => {
  agent["unionID"] = unionID;
  agent["clubID"] = clubID;
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('addAgentInfo', agent, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const updateAgentInfo = (unionID, clubID, agent) => (dispatch) => {
  agent["unionID"] = unionID;
  agent["clubID"] = clubID;
  delete agent['APlist'];
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('updateAgentInfo', agent, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const deleteAgentInfo = (unionID, clubID, id) => (dispatch) => {
  const body = {
    'id': id,
    'unionID': unionID,
    'clubID': clubID
  };
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('deleteAgentInfo', body, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const addSubAgentInfo = (unionID, clubID, agentID, subAgent) => (dispatch) => {
  subAgent["unionID"] = unionID;
  subAgent["clubID"] = clubID;
  subAgent["agentID"] = agentID;
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('addSubAgentInfo', subAgent, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const updateSubAgentInfo = (unionID, clubID, agentID, subAgent) => (dispatch) => {
  subAgent["unionID"] = unionID;
  subAgent["clubID"] = clubID;
  subAgent["agentID"] = agentID;
  delete subAgent['APlist'];
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('updateSubAgentInfo', subAgent, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const deleteSubAgentInfo = (unionID, clubID, agentID, id) => (dispatch) => {
  const body = {
    'id': id,
    'unionID': unionID,
    'clubID': clubID,
    'agentID': agentID
  };
  
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('deleteSubAgentInfo', body, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const deleteUploadedFile = (id) => (dispatch) => {
  // Use fetchJSONAction instead of fetchAction with parsePostBody
  dispatch(fetchJSONAction('deleteUploadedFile', { 'id': id }, null, () => {
    dispatch(listDBFileInfo());
  }));
};

export const deleteAllAP = () => (dispatch) => {
  dispatch(fetchAction('deleteAllAP', null, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const saveUnionAPlist = (unionID, aplist) => (dispatch) => {
  dispatch(fetchJSONAction('saveUnionAPlist', { "unionID": unionID, "aplist": aplist }, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const saveClubAPlist = (unionID, clubID, aplist) => (dispatch) => {
  dispatch(fetchJSONAction('saveClubAPlist', { "unionID": unionID, "clubID": clubID, "aplist": aplist }, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const saveAgentAPlist = (unionID, clubID, agentID, aplist) => (dispatch) => {
  dispatch(fetchJSONAction('saveAgentAPlist', { "unionID": unionID, "clubID": clubID, "agentID": agentID, "aplist": aplist }, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const saveSubAgentAPlist = (unionID, clubID, agentID, subAgentID, aplist) => (dispatch) => {
  dispatch(fetchJSONAction('saveSubAgentAPlist', { "unionID": unionID, "clubID": clubID, "agentID": agentID, "subAgentID": subAgentID, "aplist": aplist }, null, () => {
    dispatch(listUnionInfo());
  }));
};

export const getUnionsFee = () => (dispatch) => {
  dispatch(fetchAction('getUnionsFee', null, SAVE_UNIONS_FEE));
};

export const getGamesFee = () => (dispatch) => {
  dispatch(fetchAction('getGamesFee', null, SAVE_GAMES_FEE));
};

export const getSuperAgentsFee = () => (dispatch) => {
  dispatch(fetchAction('getSuperAgentsFee', null, SAVE_SUPERAGENTS_FEE));
};

export const getMembershipData = () => (dispatch) => {
  dispatch(fetchAction('getMembershipData', null, SAVE_MEMBERSHIP_SUM));
};

export const getUnionMembership = () => (dispatch) => {
  dispatch(fetchAction('getUnionMembership', null, SAVE_UNION_MEMBERSHIP));
};

export const getAgentTaxEventList = (weekString) => (dispatch) => {
  dispatch(fetchJSONAction('getAgentTaxEventList', { "weekString": weekString }, SAVE_AGENT_TAX_EVENT_LIST, null, AGENT_TAX_EVENT_LIST_FAILED));
};

export const saveCostList = (id, costList) => (dispatch) => {
  dispatch(fetchJSONAction('saveCostList', { "id": id, "costList": costList }, null, () => {
    dispatch(listRevenue());
  }));
};

export const fetchNoteToServer = (rowId, noteContent) => (dispatch) => {
  dispatch(fetchJSONAction('saveRevenueNote', { "rowId": rowId, "noteContent": noteContent }, null, () => {    
    dispatch(listRevenue());
  }));
};

export const downloadReport = (fileInfo) => async (dispatch) => {
  // Dispatch fetch start action
  dispatch({ 
    type: FETCH_START, 
    payload: { action: 'downloadFile' } 
  });
  
  try {
    // Get current access token from session storage
    const token = sessionStorage.getItem('access_token');
    
    // Check if token is expired and try to refresh if needed
    if (isTokenExpired() && getRefreshToken()) {
      try {
        const refreshed = await refreshTokenRequest();
        if (!refreshed) {
          throw new Error('Token refresh failed');
        }
      } catch (error) {
        clearTokens();
        throw new Error('Authentication failed');
      }
    }
    
    // Make direct fetch with current token
    const response = await fetch(`/api/downloadFile/${fileInfo._id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token || getAccessToken()}`
      }
    });
    
    // Handle authentication errors
    if (response.status === 401) {
      // Try refresh token if we have one
      if (getRefreshToken()) {
        try {
          const refreshed = await refreshTokenRequest();
          if (refreshed) {
            // Retry the original request with new token
            const retryResponse = await fetch(`/api/downloadFile/${fileInfo._id}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${getAccessToken()}`
              }
            });
            
            if (!retryResponse.ok) {
              throw new Error(`Failed to download: ${retryResponse.status} ${retryResponse.statusText}`);
            }
            
            // Get blob from retry response
            const blob = await retryResponse.blob();
            
            // Dispatch fetch success action
            dispatch({ 
              type: FETCH_SUCCESS, 
              payload: { action: 'downloadFile' } 
            });
            
            // Create download link and trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileInfo.reportName || 'report.zip';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            return true;
          }
        } catch (error) {
          clearTokens();
          throw new Error('Authentication failed');
        }
      }
      
      clearTokens();
      throw new Error('Authentication failed');
    }
    
    if (!response.ok) {
      throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
    }
    
    // Get blob from response
    const blob = await response.blob();
    
    // Dispatch fetch success action
    dispatch({ 
      type: FETCH_SUCCESS, 
      payload: { action: 'downloadFile' } 
    });
    
    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileInfo.reportName || 'report.zip';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    return true;
  } catch (error) {
    console.error('Error downloading file:', error);
    
    // Dispatch fetch failure action
    dispatch({ 
      type: FETCH_FAILURE, 
      payload: { action: 'downloadFile', error: error.message } 
    });
    
    // If authentication error, clear session and redirect
    if (error.message === 'Authentication failed') {
      clearTokens();
      dispatch({ type: 'LOGIN_FAILED', payload: { errormsg: 'Authentication failed' } });
    }
    
    return false;
  }
};

export const getWeeklyTotalPay = () => (dispatch) => {
  dispatch(fetchAction('getWeeklyTotalPay', null, SAVE_WEEKLY_TOTAL_PAY, null, WEEKLY_TOTAL_PAY_FAILED));
};

export const getWeeklyTotalWin = () => (dispatch) => {
  dispatch(fetchAction('getWeeklyTotalWin', null, SAVE_WEEKLY_TOTAL_WIN, null, WEEKLY_TOTAL_WIN_FAILED));
};


export const getServerConfig = () => (dispatch) => {
  dispatch(fetchAction('getServerConfig', null, SAVE_SERVER_CONFIG, null));
};

export const succeedLogin = () => (dispatch) => {
  dispatch({
    type: 'LOGIN_SUCCESS',
    payload: {
      access_token: getAccessToken(),
      refresh_token: getRefreshToken()
    }
  });
};

const silentFetchAction = (api, body, successAction, successFunction, failAction) => async (dispatch, getState) => {
  // Skip the FETCH_START action - this is the key difference from regular fetchAction
  
  try {
    const options = {
      method: body === null ? 'GET' : 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    };
    
    if (body !== null) {
      options.body = body;
    }
    
    const data = await fetchWithAuth(`/api/${api}`, options);
    
    // Action Succeed
    if (data.code === 1) {
      // Skip the FETCH_SUCCESS action too
      
      if (successAction !== null) {
        dispatch({ type: successAction, payload: data });
      } else if (successFunction) {
        successFunction(data);
      }
    } 
    // Action Failed with specific error
    else if (data.code === -1) {
      console.error('API Error:', data.msg);
      
      // Still omit the FETCH_FAILURE dispatch
      
      if (failAction) {
        dispatch({ type: failAction, payload: data });
      }
    }
    // Other failures
    else if (failAction) {
      dispatch({ type: failAction, payload: data });
    }
  } catch (error) {
    console.error('Fetch error:', error.message);
    
    // If authentication error, clear session and redirect
    if (error.message === 'Authentication failed') {
      clearTokens();
      dispatch({ type: 'LOGIN_FAILED', payload: { errormsg: 'Authentication failed' } });
    } else if (failAction) {
      dispatch({ type: failAction, payload: { error: error.message } });
    }
  }
};

// Add silent versions of key actions
export const silentListUploadedFile = () => (dispatch) => {
  dispatch(silentFetchAction('listFile', null, SAVE_UPLOADED_FILE));
};

export const silentListDBFileInfo = (page = 1, pageSize = 100) => (dispatch) => {
  dispatch(silentFetchAction(`listDBFileInfo?page=${page}&page_size=${pageSize}`, null, SAVE_FILE_INFO));
};

export const silentGetJsonWarning = () => (dispatch) => {
  dispatch(silentFetchAction('getJsonWarning', null, SAVE_JSON_WARNING));
};

// WeeklyTotalWin with lazy loading actions
export const FETCH_LATEST_UNION_DATA = 'FETCH_LATEST_UNION_DATA';
export const FETCH_PERIOD_DATA = 'FETCH_PERIOD_DATA';
export const FETCH_PERIOD_PLAYER_DATA = 'FETCH_PERIOD_PLAYER_DATA';
export const FETCH_MULTI_PERIOD_PLAYER_DATA = 'FETCH_MULTI_PERIOD_PLAYER_DATA';
export const FETCH_CLUBS_FOR_UNION = 'FETCH_CLUBS_FOR_UNION';
export const FETCH_AGENTS_FOR_CLUB = 'FETCH_AGENTS_FOR_CLUB';
export const FETCH_SUBAGENTS_FOR_AGENT = 'FETCH_SUBAGENTS_FOR_AGENT';
export const FETCH_CHART_DATA = 'FETCH_CHART_DATA';
export const FETCH_MULTI_PERIOD_DATA = 'FETCH_MULTI_PERIOD_DATA';

/**
 * Fetch only the latest week's union data
 * Initial load for the WeeklyTotalWin component
 */
export const fetchLatestUnionData = () => (dispatch) => {
  dispatch(fetchAction('getLatestUnionData', null, FETCH_LATEST_UNION_DATA, null, WEEKLY_TOTAL_WIN_FAILED));
};

/**
 * Fetch data for a specific period with hierarchy information
 * @param {number} periodIndex - Index of the period to load
 * @param {object} hierarchyPath - Current position in hierarchy {hierarchyLevel, unionId, clubId, agentId}
 */
export const fetchPeriodData = (periodIndex, hierarchyPath) => (dispatch) => {
  const body = {
    periodIndex: periodIndex,
    hierarchyLevel: hierarchyPath.hierarchyLevel,
    unionId: hierarchyPath.unionId,
    clubId: hierarchyPath.clubID,
    agentId: hierarchyPath.agentId
  };
  
  dispatch(fetchJSONAction('getPeriodData', body, FETCH_PERIOD_DATA, null, WEEKLY_TOTAL_WIN_FAILED));
};

export const fetchPeriodPlayerData = (periodIndex, hierarchyPath, sort_by = "fee", sort_order = -1, page = 1, page_size = 50) => (dispatch) => {
  const body = {
    periodIndex: periodIndex,
    hierarchyLevel: hierarchyPath.hierarchyLevel,
    unionId: hierarchyPath.unionId,
    clubId: hierarchyPath.clubId,
    superAgentId: hierarchyPath.superAgentId,
    agentId: hierarchyPath.agentId,
    sort_by: sort_by,
    sort_order: sort_order,
    page: page,
    page_size: page_size
  };
    
  dispatch(fetchJSONAction('getPeriodPlayerData', body, FETCH_PERIOD_PLAYER_DATA, null, WEEKLY_TOTAL_WIN_FAILED));
};

export const fetchMultiPeriodPlayerData = (startIndex, endIndex, hierarchyPath, sort_by = "fee", sort_order = -1, page = 1, page_size = 50) => (dispatch) => {
  const body = {
    startIndex: startIndex,
    endIndex: endIndex,
    hierarchyLevel: hierarchyPath.hierarchyLevel,
    unionId: hierarchyPath.unionId,
    clubId: hierarchyPath.clubId,
    superAgentId: hierarchyPath.superAgentId,
    agentId: hierarchyPath.agentId,
    sort_by: sort_by,
    sort_order: sort_order,
    page: page,
    page_size: page_size
  };
    
  dispatch(fetchJSONAction('getMultiPeriodPlayerData', body, FETCH_MULTI_PERIOD_PLAYER_DATA, null, WEEKLY_TOTAL_WIN_FAILED));
};

/**
 * Fetch historical data for charts
 * Called when user switches to chart view or changes chart parameters
 * @param {string} timeRange - Time range for the chart (week, month, quarter, year, all)
 * @param {object} hierarchyPath - Current position in hierarchy {hierarchyLevel, unionId, clubId, agentId}
 */
export const fetchChartData = (timeRange, hierarchyPath) => (dispatch) => {
  const body = {
    timeRange,
    hierarchyPath
  };
  
  dispatch(fetchJSONAction('getChartData', body, FETCH_CHART_DATA, null, WEEKLY_TOTAL_WIN_FAILED));
};

/**
 * Fetch data for multiple periods with hierarchy information
 * @param {Array} periodRange - Array containing start and end period indices [startIndex, endIndex]
 * @param {object} hierarchyPath - Current position in hierarchy {hierarchyLevel, unionId, clubId, agentId}
 */
export const fetchMultiPeriodData = (periodRange, hierarchyPath) => (dispatch) => {
  const [startIndex, endIndex] = periodRange;
  const body = {
    startIndex,
    endIndex,
    hierarchyLevel: hierarchyPath.hierarchyLevel,
    unionId: hierarchyPath.unionId,
    clubId: hierarchyPath.clubId,
    agentId: hierarchyPath.agentId
  };
  
  dispatch(fetchJSONAction('getMultiPeriodData', body, FETCH_MULTI_PERIOD_DATA, null, WEEKLY_TOTAL_WIN_FAILED));
};

export const fetchMultiChartData = (timeRange, hierarchyPath) => (dispatch) => {
  const body = {
    timeRange,
    hierarchyPath
  };
  
  dispatch(fetchJSONAction('getMultiChartData', body, FETCH_PERIOD_DATA, null, WEEKLY_TOTAL_WIN_FAILED));
};

// Add new action type for entity search
export const SEARCH_ENTITY = 'SEARCH_ENTITY';

// Add new action creator for entity search
export const searchEntity = (level, query) => (dispatch) => {
  const body = {
    level,
    query
  };
  
  dispatch(fetchJSONAction('searchEntity', body, SEARCH_ENTITY, null, WEEKLY_TOTAL_WIN_FAILED));
};

// Add new action type for loading data by hierarchy
export const LOAD_DATA_BY_HIERARCHY = 'LOAD_DATA_BY_HIERARCHY';

// Add new action creator for loading data by hierarchy
export const loadDataByHierarchy = (hierarchyPath, periodSelectionMode, periodRange, currentPeriodIndex) => (dispatch) => {
  // Build the appropriate hierarchy path object
  const path = {
    hierarchyLevel: hierarchyPath.level,
    level: hierarchyPath.level,
    unionId: hierarchyPath.unionId,
    clubId: hierarchyPath.clubId,
    agentId: hierarchyPath.agentId
  };
  
  // Load data based on current period selection mode
  if (periodSelectionMode === 'multi') {
    dispatch(fetchMultiPeriodData(periodRange, path));
  } else {
    dispatch(fetchPeriodData(currentPeriodIndex, path));
  }
};

// Add new action creator to clear multi-period data
export const clearMultiPeriodData = () => (dispatch) => {
  dispatch({ type: CLEAR_MULTI_PERIOD_DATA });
};

export const getFlashLevelData = () => (dispatch) => {
  dispatch(fetchJSONAction('getFlashLevelData', null, SAVE_FLASH_LEVEL_DATA, null, FLASH_LEVEL_DATA_FAILED));
};  
// Add new action creator to reset weekly total win data
export const resetWeeklyTotalWinData = () => (dispatch) => {
  dispatch({ type: RESET_WEEKLY_TOTAL_WIN_DATA });
};


